import clientEnv from '../../../utils/global/clientEnv'
import { OAUTH_ERROR_CODES } from '../../../utils/auth/authConstants'
import { trackEvent } from '../../../utils/events/trackEvent'
import { getDeviceFingerprintHeader } from './deviceFingerprint'
import { getRefreshToken, setRefreshToken } from './authStorage'

/**
 * Custom error class for token refresh failures with structured information
 */
export class TokenRefreshError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly errorCode?: string,
    public readonly responseText?: string
  ) {
    super(message)
    this.name = 'TokenRefreshError'
  }
}

export class TokenRefreshService {
  private static isRefreshing = false
  private static refreshPromise: Promise<string> | null = null

  /**
   * Attempts to refresh the access token using the HttpOnly refresh token cookie.
   * Includes retry logic to handle cross-tab race conditions.
   */
  static async refreshAccessToken(): Promise<string> {
    // Prevent multiple simultaneous refresh attempts in the same tab
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise
    }

    this.isRefreshing = true
    this.refreshPromise = this.attemptRefreshWithRetry()

    try {
      return await this.refreshPromise
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  private static async attemptRefreshWithRetry(retryCount = 0): Promise<string> {
    const MAX_RETRIES = clientEnv.PUBLIC_CLIENT_TOKEN_REFRESH_MAX_RETRIES
    const RETRY_DELAY = clientEnv.PUBLIC_CLIENT_TOKEN_REFRESH_RETRY_DELAY_MS

    // Track initial attempt (not retries)
    if (retryCount === 0) {
      trackEvent({
        id: 'auth.refresh.attempted',
        description: 'Token refresh attempted',
      })
    }

    try {
      // Generate device fingerprint for this request
      const deviceFingerprint = getDeviceFingerprintHeader()

      const refreshToken = await getRefreshToken()

      if (!refreshToken) {
        trackEvent({
          id: 'auth.refresh.no_token',
          description: 'No refresh token available',
        })
        throw new TokenRefreshError('No refresh token available', undefined, 'no_refresh_token')
      }

      const response = await fetch(`${clientEnv.PUBLIC_CLIENT_AUTH_SERVICE_BASE_URL}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Device-Fingerprint': deviceFingerprint,
        },
        body: new URLSearchParams({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          grant_type: 'refresh_token',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          client_id: clientEnv.PUBLIC_CLIENT_AUTH_SERVICE_CLIENT_ID,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          refresh_token: refreshToken,
        }),
        credentials: 'include',
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error')

        // Try to parse OAuth error from response
        let errorCode: string | undefined
        try {
          const errorData = JSON.parse(errorText)
          errorCode = errorData.error
        } catch {
          // Not JSON, use raw text
        }

        throw new TokenRefreshError(
          `HTTP ${response.status}: ${errorText}`,
          response.status,
          errorCode,
          errorText
        )
      }

      const data = await response.json()

      if (data.refresh_token) {
        await setRefreshToken(data.refresh_token)
      }

      trackEvent({
        id: 'auth.refresh.success',
        description: 'Token refresh succeeded',
        data: { retryCount },
      })

      // Return ID token to maintain existing behavior (session.token is the ID token)
      // TODO: In a future PR, switch to using access_token for API calls
      return data.id_token
    } catch (error) {
      if (retryCount < MAX_RETRIES && this.shouldRetry(error)) {
        trackEvent({
          id: 'auth.refresh.retried',
          description: 'Token refresh retry attempted',
          data: {
            retryCount: retryCount + 1,
            errorCode: error instanceof TokenRefreshError ? error.errorCode : undefined,
            statusCode: error instanceof TokenRefreshError ? error.statusCode : undefined,
          },
        })

        // Brief delay to let other tab's refresh complete
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))

        return this.attemptRefreshWithRetry(retryCount + 1)
      }

      // Final failure after all retries exhausted
      trackEvent({
        id: 'auth.refresh.failed',
        description: 'Token refresh failed after all retries',
        data: {
          retryCount,
          errorCode: error instanceof TokenRefreshError ? error.errorCode : undefined,
          statusCode: error instanceof TokenRefreshError ? error.statusCode : undefined,
        },
      })

      throw error
    }
  }

  private static shouldRetry(error: unknown): boolean {
    // Handle structured TokenRefreshError
    if (error instanceof TokenRefreshError) {
      const { statusCode, errorCode, responseText } = error

      if (errorCode === 'no_refresh_token') {
        return false
      }

      // Don't retry on missing refresh token (user not in roulette)
      if (responseText?.toLowerCase().includes('missing required parameter')) {
        return false
      }

      // Don't retry on HTTP 400 Bad Request (except invalid_grant which is cross-tab race)
      if (statusCode === 400 && errorCode !== OAUTH_ERROR_CODES.INVALID_GRANT) {
        return false
      }

      // Retry everything else (5xx, 401, 403, invalid_grant, network errors, etc.)
      return true
    }

    // Handle generic errors (network errors, etc.) - always retry
    return true
  }
}
